/**
 * Optimizer Control Center — row mutations with toasts + refetch
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  applyOptimizer,
  deleteConfig,
  setEnabled,
  setKillSwitch,
} from '@/features/flow-optimizer/services/flow-optimizer-api';
import { applyRotation } from '@/features/flow-optimizer/services/flow-optimizer-queue-api';
import { queueKeys } from '@/features/flow-optimizer/hooks/useFlowOptimizerQueue';
import { optimizerCenterKeys } from '@/features/optimizer-center/hooks/useOptimizerConfigs';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { applyHeldReason } from '@/features/flow-optimizer/apply-report';
import { queueProviderOf } from '@/features/trackers/flow-optimizer';
import { getApiErrorMessage } from '@/lib/utils';
import { queueHeldError } from '@/features/flow-optimizer/tabs/queue/rotation/outcome/rotation-notice';
import { useToast } from '@/providers/ToastProvider';

function errorStatus(err: unknown): number | undefined {
  const resp = (err as { response?: { status?: number } } | null)?.response;
  return typeof resp?.status === 'number' ? resp.status : undefined;
}

/** 409 = kill switch blocked the push; everything else falls through. */
function applyErrorMessage(err: unknown): string {
  if (errorStatus(err) === 409) {
    return 'Pushes are paused for this campaign (kill switch on). Resume pushes first.';
  }
  return getApiErrorMessage(err, 'Failed to apply optimizer');
}

/** What the ⚡ "Update now" run includes — the user picks in the dialog. */
export interface UpdateNowChoice {
  weights: boolean;
  rotation: boolean;
}

const choiceSummary = ({ weights, rotation }: UpdateNowChoice): string =>
  weights && rotation ? 'Weights + queue updated' : weights ? 'Weights updated' : 'Queue rotated';

export function useRowActions(row: OptimizerRow) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: optimizerCenterKeys.all });

  // "Update now" runs the steps the user PICKED (both = exactly the scheduled
  // pass): rebalance weights, then rotate the queue (rules + cap + promote).
  // A tracker without a queue (Tres Commas) never rotates.
  const queue = queueProviderOf(row.provider);
  const updateNow = useMutation({
    mutationFn: async ({ weights, rotation }: UpdateNowChoice) => {
      const result = weights ? await applyOptimizer(row.campaignId, row.provider) : null;
      if (applyHeldReason(row.provider, result)) return result;
      const rotated = rotation && queue ? await applyRotation(row.campaignId, queue) : null;
      return result ?? rotated;
    },
    onSuccess: (result, choice) => {
      // A held Tres Commas apply answers 201 applied:false — never "applied".
      const held = applyHeldReason(row.provider, result);
      if (held) toast.warning('Nothing pushed', held);
      else toast.success('Optimizer applied', `${choiceSummary(choice)} for ${row.campaignName}`);
      refresh();
      if (!queue) return;
      void queryClient.invalidateQueries({ queryKey: queueKeys.queue(queue, row.campaignId) });
    },
    onError: (err) => {
      const held = queueHeldError(row.provider, err);
      if (held) toast.warning('Nothing pushed', held);
      else toast.error('Update now failed', applyErrorMessage(err));
    },
  });

  const toggleKillSwitch = useMutation({
    mutationFn: () => setKillSwitch(row.campaignId, !row.killSwitch, row.provider),
    onSuccess: () => {
      toast.success(row.killSwitch ? 'Pushes resumed' : 'Pushes paused');
      refresh();
    },
    onError: (err) =>
      toast.error('Failed to toggle pause', getApiErrorMessage(err)),
  });

  const setAutoRun = useMutation({
    mutationFn: (enabled: boolean) =>
      setEnabled(row.campaignId, enabled, row.provider),
    onSuccess: (_data, enabled) => {
      toast.success(enabled ? 'Auto-run enabled' : 'Auto-run disabled');
      refresh();
    },
    onError: (err) =>
      toast.error('Failed to toggle auto-run', getApiErrorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: () => deleteConfig(row.campaignId, row.provider),
    onSuccess: () => {
      toast.success('Optimizer config removed', row.campaignName);
      refresh();
    },
    onError: (err) =>
      toast.error('Failed to remove config', getApiErrorMessage(err)),
  });

  return { updateNow, toggleKillSwitch, setAutoRun, remove };
}
